import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './WeddingPlanner.css';

interface PersonCheck {
    passed: boolean;
    method: string;
    rashi?: string;
    rashiHindi?: string;
    lord?: string;
    nakshatra?: string;
    house?: number;
    detail: string;
}

interface MahuratResult {
    date: string;
    approved: boolean;
    bride: PersonCheck;
    groom: PersonCheck;
}

interface MethodsUsed {
    bride: string;
    groom: string;
}

const WeddingPlanner: React.FC = () => {
    const { accessToken, logout } = useAuth();
    const history = useHistory();
    const [brideName, setBrideName] = useState('');
    const [groomName, setGroomName] = useState('');
    const [brideDate, setBrideDate] = useState('');
    const [groomDate, setGroomDate] = useState('');
    const [targetMonth, setTargetMonth] = useState('');
    const [approved, setApproved] = useState<MahuratResult[]>([]);
    const [rejected, setRejected] = useState<MahuratResult[]>([]);
    const [methods, setMethods] = useState<MethodsUsed | null>(null);
    const [expandedIdx, setExpandedIdx] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const getYesterdayDateString = () => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yyyy = yesterday.getFullYear();
        const mm = String(yesterday.getMonth() + 1).padStart(2, '0');
        const dd = String(yesterday.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const handleCalculate = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setApproved([]);
        setRejected([]);
        setMethods(null);
        setExpandedIdx(null);
        
        if (!targetMonth) {
            setError('Please select a target month.');
            return;
        }

        if ((!brideDate && !brideName) || (!groomDate && !groomName)) {
            setError('Please enter either Date of Birth or Name for both Bride and Groom.');
            return;
        }

        const maxDate = getYesterdayDateString();
        if (brideDate && brideDate > maxDate) {
            setError("Bride's Date of Birth must be in the past.");
            return;
        }
        if (groomDate && groomDate > maxDate) {
            setError("Groom's Date of Birth must be in the past.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/wedding-planner/shubh-mahurat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ brideName, groomName, brideDateOfBirth: brideDate, groomDateOfBirth: groomDate, targetMonth }),
            });

            const data = await res.json();
            if (res.ok) {
                setApproved(data.approved || []);
                setRejected(data.rejected || []);
                setMethods(data.methods || null);
            } else {
                if (res.status === 401 || res.status === 403) {
                    logout();
                    history.push('/login');
                } else {
                    setError(data.message || 'Failed to calculate Shubh Mahurat.');
                }
            }
        } catch (err: any) {
             setError('Failed to connect to server.');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (key: string) => {
        setExpandedIdx(prev => prev === key ? null : key);
    };

    const renderReasonCard = (person: PersonCheck, label: string) => (
        <div className={`reason-card ${person.passed ? 'pass' : 'fail'}`}>
            <div className="reason-card-header">
                <span className="reason-person">{label}</span>
                <span className={`reason-badge ${person.passed ? 'approved' : 'rejected'}`}>
                    {person.passed ? '✅ Passed' : '❌ Failed'}
                </span>
            </div>
            <div className="reason-meta">
                <span className="reason-tag">Method: <strong>{person.method}</strong></span>
                {person.rashi && <span className="reason-tag">Rashi: <strong>{person.rashi} ({person.rashiHindi})</strong></span>}
                {person.lord && <span className="reason-tag">Lord: <strong>{person.lord}</strong></span>}
                {person.nakshatra && <span className="reason-tag">Nakshatra: <strong>{person.nakshatra}</strong></span>}
                {person.house && <span className="reason-tag">House: <strong>{person.house}</strong></span>}
            </div>
            <p className="reason-detail">{person.detail}</p>
        </div>
    );

    const renderDateItem = (item: MahuratResult, idx: number, type: 'approved' | 'rejected') => {
        const d = new Date(item.date);
        const key = `${type}-${idx}`;
        const isExpanded = expandedIdx === key;

        return (
            <div key={key} className={`mahurat-item-card ${type}`}>
                <div className="mahurat-item-main" onClick={() => toggleExpand(key)}>
                    <span className="mahurat-icon">{type === 'approved' ? '🕊️' : '⚠️'}</span>
                    <span className="mahurat-date">
                        {d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                    <button className="reason-toggle-btn" type="button">
                        {isExpanded ? '▲ Hide Reason' : '▼ View Reason'}
                    </button>
                </div>
                {isExpanded && (
                    <div className="mahurat-reason-panel">
                        {renderReasonCard(item.bride, '👰 Bride')}
                        {renderReasonCard(item.groom, '🤵 Groom')}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="wedding-planner-page">
            <div className="wedding-planner-container">
                <div className="planner-header">
                    <h1>Mahurat</h1>
                    <p>Find your Shubh Mahurat with the stars.</p>
                </div>

                <div className="premium-card planner-card">
                    <div className="card-header">
                        <span className="card-icon">✨</span>
                        <h3 className="card-title">Get Shubh Mahurat</h3>
                    </div>
                    <form onSubmit={handleCalculate} className="planner-form">
                        <div className="info-hint">
                            <span className="hint-icon">💡</span>
                            <span className="hint-text">
                                <strong>Personalized Mahurat:</strong> If DOB is provided, <em>Chandra Shuddhi</em> (Moon transit check) is used.
                                If only the name is provided, <em>Name Rashi</em> (first-letter zodiac mapping) is used instead.
                            </span>
                        </div>
                        <div className="form-group-row">
                            <div className="form-group half-width">
                                <label>Bride's Name</label>
                                <input 
                                    type="text" 
                                    value={brideName} 
                                    onChange={(e) => setBrideName(e.target.value)}
                                    placeholder="Enter Name"
                                />
                            </div>
                            <div className="form-group half-width">
                                <label>Bride's Date of Birth (Opt)</label>
                                <input 
                                    type="date" 
                                    value={brideDate} 
                                    onChange={(e) => setBrideDate(e.target.value)}
                                    max={getYesterdayDateString()}
                                />
                            </div>
                        </div>

                        <div className="form-group-row">
                            <div className="form-group half-width">
                                <label>Groom's Name</label>
                                <input 
                                    type="text" 
                                    value={groomName} 
                                    onChange={(e) => setGroomName(e.target.value)}
                                    placeholder="Enter Name"
                                />
                            </div>
                            <div className="form-group half-width">
                                <label>Groom's Date of Birth (Opt)</label>
                                <input 
                                    type="date" 
                                    value={groomDate} 
                                    onChange={(e) => setGroomDate(e.target.value)}
                                    max={getYesterdayDateString()}
                                />
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label>Target Wedding Month</label>
                            <input 
                                type="month" 
                                value={targetMonth} 
                                onChange={(e) => setTargetMonth(e.target.value)}
                                required 
                                className="input-month"
                            />
                        </div>
                        {error && <div className="error-message">{error}</div>}
                        <button type="submit" className="primary-btn-premium" disabled={loading}>
                            {loading ? 'Calculating Stars...' : 'Find Shubh Mahurat'}
                        </button>
                    </form>

                    {methods && (
                        <div className="methods-banner">
                            <h4>Methods Used</h4>
                            <div className="methods-row">
                                <span className="method-chip">👰 Bride: {methods.bride}</span>
                                <span className="method-chip">🤵 Groom: {methods.groom}</span>
                            </div>
                        </div>
                    )}

                    {approved.length > 0 && (
                        <div className="mahurat-results">
                            <h4>🕊️ Auspicious Dates Found ({approved.length})</h4>
                            <div className="mahurat-list">
                                {approved.map((item, idx) => renderDateItem(item, idx, 'approved'))}
                            </div>
                        </div>
                    )}

                    {rejected.length > 0 && (
                        <div className="mahurat-results rejected">
                            <h4>⚠️ Avoided Dates ({rejected.length})</h4>
                            <div className="mahurat-list">
                                {rejected.map((item, idx) => renderDateItem(item, idx, 'rejected'))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WeddingPlanner;
